import type { PushDevice } from "@beacon/core/chat-push"
import type { InstallRoute } from "@/installed-app/install-route.ts"

/*
 * The rules of Chat push on this browser, apart from the browser (`push-device.ts` asks it): where
 * push stands for the Member, and the stage's key as a browser takes it.
 */

/**
 * Where push stands on this browser, for the Member:
 *
 * - `unsupported`: this browser can't push, and nothing would change that;
 * - `install-first`: on an iPhone or iPad, only Beacon added to the Home Screen can push;
 * - `not-set-up`: this Beacon's push isn't set up (its stage's keys aren't set);
 * - `blocked`: the Member said no to this browser's notifications, which only its settings undo;
 * - `off`: it can, once the Member turns it on;
 * - `on`: it notifies here, with previews or without.
 */
export type PushState =
  | { state: "unsupported" }
  | { state: "install-first" }
  | { state: "not-set-up" }
  | { state: "blocked" }
  | { state: "off" }
  | { state: "on"; previews: boolean }

/** What the browser and the server say, from which `pushStateOf` decides. */
export type PushFacts = {
  /** The browser has service workers, the Push API and notifications. */
  supported: boolean
  /** How it installs Beacon (`installRoute`): iOS only pushes once installed. */
  installRoute: InstallRoute
  /** The stage's public key; none while its push is off. */
  publicKey: string | null
  permission: NotificationPermission
  /** This browser is registered with the server: whether its pushes show previews, or none. */
  registered: PushDevice | null
}

export function pushStateOf(facts: PushFacts): PushState {
  if (!facts.supported)
    return facts.installRoute === "ios-share" || facts.installRoute === "open-in-browser"
      ? { state: "install-first" }
      : { state: "unsupported" }
  if (!facts.publicKey) return { state: "not-set-up" }
  if (facts.permission === "denied") return { state: "blocked" }
  if (facts.permission === "granted" && facts.registered)
    return { state: "on", previews: facts.registered.previews }
  return { state: "off" }
}

/** A VAPID public key, as `pushManager.subscribe` takes it: its bytes, from unpadded base64url. */
export function keyBytes(publicKey: string): Uint8Array<ArrayBuffer> {
  const base64 = publicKey.replace(/-/g, "+").replace(/_/g, "/")
  const binary = atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4))
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/** Whether a subscription was made with `publicKey`: one made with an older key can't be pushed to. */
export function madeWith(subscription: PushSubscription, publicKey: string) {
  const key = subscription.options.applicationServerKey
  if (!key) return false
  const made = new Uint8Array(key)
  const wanted = keyBytes(publicKey)
  return made.length === wanted.length && made.every((byte, i) => byte === wanted[i])
}
